import React, { useEffect, useState } from 'react';
import { Table, Input, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLocation } from 'react-router-dom';
import { getLabEarningsReport, getReportApiByKey } from '@app/api/reports.api';

const { Title } = Typography;

interface Test {
  test_name: string;
  test_price: number;
}

interface Category {
  category_name: string;
  category_total: number;
  'Main Tests': Test[];
}

interface Division {
  division_total: number;
  Categories: Category[];
}

interface RawData {
  report: {
    total: number;
    [divisionName: string]: any;
  };
}

interface TableRow {
  key: string;
  name: string;
  total: number;
  level: number;
  children?: TableRow[];
}

interface LabReportTableProps {
  reportKey: string;
  filters: any;
}

const reportColumnsMap: Record<string, { totalTitle: string; isCurrency: boolean }> = {
  'earnings-by-divisions': { totalTitle: 'Total Earnings (LKR)', isCurrency: true },
  'test-count-by-divisions': { totalTitle: 'Total Count', isCurrency: false },
  'earnings-by-divisions-nom-pay': { totalTitle: 'Total Earnings (LKR)', isCurrency: true },
  'test-count-by-divisions-nom-pay': { totalTitle: 'Total Count', isCurrency: false },
};

// SearchFilter component embedded here for completeness
const SearchFilter: React.FC<{ onSearch: (keyword: string) => void }> = ({ onSearch }) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(e.target.value);
  };

  const handleSearch = (value: string) => {
    onSearch(value);
  };

  return (
    <div className="hide-in-pdf" style={{ marginBottom: 16 }}>
      <Input.Search
        placeholder="Search divisions, categories or tests..."
        allowClear
        onChange={handleInputChange}
        onSearch={handleSearch}
        enterButton
        style={{ width: 300 }}
        size="small"
      />
    </div>
  );
};

export const LabReportTable: React.FC<LabReportTableProps> = ({ reportKey, filters }) => {
  const [rawData, setRawData] = useState<RawData | null>(null);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [filteredData, setFilteredData] = useState<TableRow[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const location = useLocation();
  const reportType = location.state?.reportType;

  const columnConfig = reportColumnsMap[reportKey];

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
    .division-row td { background-color: #ebedef !important; }
    .category-row td { background-color: #f8f9f9 !important; }
    .type-row td { background-color: #ffffff !important; }
  `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!filters || !reportKey) return;

      const reportApi = getReportApiByKey(reportKey);
      if (!reportApi) {
        message.error('Invalid report key.');
        return;
      }

      try {
        const data = await reportApi.generateReport(filters);
        setRawData(data);
      } catch (err) {
        message.error('Failed to fetch report data.');
        console.error(err);
      }
    };

    fetchData();
  }, [filters, reportKey]);

  useEffect(() => {
    if (!rawData) return;

    const divisions = rawData?.report ?? {};
    const rows: TableRow[] = [];

    Object.entries(divisions).forEach(([divisionId, divisionData]) => {
      const divisionRow: TableRow = {
        key: divisionId,
        name: (divisionData as any).name || `Division ${divisionId}`,
        total: (divisionData as any).total || 0,
        level: 0,
        children: [],
      };

      const samples = (divisionData as any).samples ?? {};
      Object.entries(samples).forEach(([sampleId, sampleData]) => {
        const sample = sampleData as any;

        const sampleRow: TableRow = {
          key: `${divisionId}-sample-${sampleId}`,
          name: sample.name || `Sample ${sampleId}`,
          total: sample.total || 0,
          level: 1,
          children: [],
        };

        const categories = sample.categories ?? {};
        Object.entries(categories).forEach(([catId, catData]) => {
          const category = catData as any;

          const categoryRow: TableRow = {
            key: `${divisionId}-sample-${sampleId}-cat-${catId}`,
            name: category.name || `Category ${catId}`,
            total: category.total || 0,
            level: 2,
          };

          sampleRow.children?.push(categoryRow);
        });

        divisionRow.children?.push(sampleRow);
      });

      rows.push(divisionRow);
    });

    setTableData(rows);
    setFilteredData(rows);
    setExpandedRowKeys(collectAllKeys(rows));
  }, [rawData]);

  const collectAllKeys = (rows: TableRow[]): string[] => {
    let keys: string[] = [];
    for (const row of rows) {
      keys.push(row.key);
      if (row.children) {
        keys = keys.concat(collectAllKeys(row.children));
      }
    }
    return keys;
  };

  const handleSearch = (keyword: string) => {
    if (!keyword || keyword.trim() === '') {
      setFilteredData(tableData);
      return;
    }

    const filtered = searchInTree(tableData, keyword.trim());
    setFilteredData(filtered);
  };

  const searchInTree = (nodes: TableRow[], keyword: string): TableRow[] => {
    const results: TableRow[] = [];

    for (const division of nodes) {
      const divisionMatches = division.name.toLowerCase().includes(keyword.toLowerCase());

      // Check if any categories or tests match
      const filteredCategories: TableRow[] = [];

      if (division.children) {
        for (const category of division.children) {
          const categoryMatches = category.name.toLowerCase().includes(keyword.toLowerCase());

          // Check if any tests match
          const filteredTests: TableRow[] = [];

          if (category.children) {
            for (const test of category.children) {
              const testMatches = test.name.toLowerCase().includes(keyword.toLowerCase());
              if (testMatches) {
                filteredTests.push(test);
              }
            }
          }

          // Include category if it matches OR if it has matching tests
          if (categoryMatches || filteredTests.length > 0) {
            filteredCategories.push({
              ...category,
              children: filteredTests.length > 0 ? filteredTests : category.children,
            });
          }
        }
      }

      // Include division if it matches OR if it has matching categories/tests
      if (divisionMatches || filteredCategories.length > 0) {
        results.push({
          ...division,
          children: filteredCategories.length > 0 ? filteredCategories : division.children,
        });
      }
    }

    return results;
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (value, row) => {
        const level = row.key.split('-').length - 1;
        return (
          <div
            style={{
              paddingLeft: `${level * 16}px`,
              fontWeight: level === 0 ? 'bold' : 'normal',
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      title: columnConfig.totalTitle,
      dataIndex: 'total',
      key: 'total',
      render: (value) => (columnConfig.isCurrency ? `${value.toLocaleString()}/=` : value.toLocaleString()),
    },
  ];

  return (
    <div>
      <Title level={4}>{columnConfig.totalTitle.split('(')[0]} by Divisions</Title>
      <SearchFilter onSearch={handleSearch} />
      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={false}
        rowKey="key"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
        }}
        rowClassName={(record) => {
          if (record.level === 0) return 'division-row';
          if (record.level === 1) return 'category-row';
          if (record.level === 2) return 'test-row';
          return '';
        }}
      />
    </div>
  );
};
